import { UNIFAC } from "./CoeficienteAtividade";
import { Antoine } from "./PressaoSaturacao";
import Substancia from "./Substancia";

// Tipagem intermediária da classe
export type ComposicaoUA = {
    substancia: Substancia,
    fracao: number,
    subgrupos: number[],
    antoine: {
        A: number,
        B: number,
        C: number
    }
};

export default class ELV {

    pressao = 0;

    // Unidade de temperatura: K
    temperatura = 0;
    
    // Representa o erro relativo, em porcentagem
    erro = 0.001;

    setTemperatura(temp: number) {
        if(temp > 0)
            this.temperatura = temp;
        return this;
    }

    setPressao(press: number) {
        if(press > 0)
            this.pressao = press;
        return this;
    }

    setErro(erro: number) {
        if(erro > 0 && erro <= 100)
            this.erro = erro;
        return this;
    }

    erroRelativo(valor: number, novoValor: number) {
        return Math.abs(valor - novoValor) * 100 / novoValor;
    }

    // Recebe a fração na fase líquida
    bolp(temperatura: number, composicao: ComposicaoUA[]) {
        // ATENÇÃO: AS FRAÇÕES PASSADAS SÃO CORRESPONDENTES A FRASE LÍQUIDA
        // Calcula a pressão de saturação de cada componente
        const psat: number[] = [];
        composicao.forEach(comp => {
            let antoine = new Antoine(comp.substancia.nome, comp.substancia.formulaQuimica);
            antoine.setConstantes(comp.antoine.A, comp.antoine.B, comp.antoine.C);
            let pressao = antoine.calcularPressao(temperatura - 273.15);
            psat.push(pressao);
        });

        // Calcula o coeficiente de atividade para cada componente
        const unifac = new UNIFAC();
        unifac.setTemperatura(temperatura);
        composicao.forEach(comp => {
            unifac.add({
                substancia: comp.substancia, subgrupos: comp.subgrupos, fracao: comp.fracao
            });
        });
        const gama = unifac.calcular();

        // Estima a primeira pressão do sistema
        let bolp = 0;
        composicao.forEach((comp, indc) => {
            bolp += comp.fracao * gama[indc] * psat[indc];
        });

        // Executa o processo iterativo
        let e = 1;
        let fracaoGas: number[] = [];
        while(e > this.erro) {
            // Encontra as frações na fase gás
            fracaoGas = [];
            for(let i = 0; i < composicao.length; i++) {
                fracaoGas.push(composicao[i].fracao * gama[i] * psat[i] / bolp);
            }

            // Encontra uma nova pressão
            let novop = 0;
            for(let i = 0; i < composicao.length; i++) {
                novop += fracaoGas[i] / (gama[i] * psat[i]);
            }
            novop = 1 / novop;

            // Calcula o erro
            e = this.erroRelativo(bolp, novop);
            bolp = novop;
        }

        // Organiza a resposta
        let sistema: { substancia: Substancia, fracaoLiq: number, fracaoGas: number }[] = [];
        composicao.forEach((comp, i) => {
            sistema.push({
                substancia: comp.substancia,
                fracaoLiq: comp.fracao,
                fracaoGas: fracaoGas[i]
            });
        });

        return {
            temperatura, pressao: bolp, sistema
        };
    }

    bolt(pressao: number, composicao: ComposicaoUA[]) {
        // ATENÇÃO: AS FRAÇÕES PASSADAS SÃO CORRESPONDENTES A FRASE LÍQUIDA
        // Estimativa inicial para temperatura
        const unifac = new UNIFAC();
        const antoine = new Antoine("Genérico", "CH4");
        let bolt = 273.15;
        composicao.forEach(comp => {
            antoine.setConstantes(comp.antoine.A, comp.antoine.B, comp.antoine.C);
            bolt += comp.fracao * antoine.calcularTemperatura(pressao);

            // Como fração do líquido é definida no parâmetro, define o sistema unifac
            unifac.add({
                subgrupos: comp.subgrupos, substancia: comp.substancia, fracao: comp.fracao
            });
        });

        // Cálculo dos coeficientes de atividade e pressões de saturação estimados
        unifac.setTemperatura(bolt);
        let gama = unifac.calcular();
        let psat: number[] = [];
        composicao.forEach(comp => {
            antoine.setConstantes(comp.antoine.A, comp.antoine.B, comp.antoine.C);
            psat.push(antoine.calcularPressao(bolt - 273.15));
        });

        // Cria uma variável de iteração
        // Convenção: o primeiro componente se torna o pivot
        let pjsat = psat[0];
        let soma = 0;
        for(let i = 0; i < composicao.length; i++) {
            soma += composicao[i].fracao * gama[i] * psat[i] / pjsat;
        }
        pjsat = pressao / soma;

        // Calcula a temperatura para iniciar o processo iterativo
        antoine.setConstantes(composicao[0].antoine.A, composicao[0].antoine.B, composicao[0].antoine.C);
        bolt = antoine.calcularTemperatura(pjsat) + 273.15;

        // Inicia o processo iterativo
        let e = 1;
        let fracaoGas: number[] = [];
        while(e > this.erro) {
            psat = [];
            fracaoGas = [];

            // Calcula novas pressões de saturação e frações na fase gás
            for(let i = 0; i < composicao.length; i++) {
                antoine.setConstantes(composicao[i].antoine.A, composicao[i].antoine.B, composicao[i].antoine.C)
                psat.push(antoine.calcularPressao(bolt - 273.15));
                fracaoGas.push(composicao[i].fracao * gama[i] * psat[i] / bolt);
            }

            // Com nova temperatura, calcula coeficiente de atividade
            unifac.setTemperatura(bolt);
            gama = unifac.calcular();

            // Redefine a variável de iteração, com primeiro elemento como pivot
            pjsat = psat[0];
            soma = 0;
            for(let i = 0; i < composicao.length; i++) {
                soma += composicao[i].fracao * gama[i] * psat[i] / pjsat;
            }
            pjsat = pressao / soma;

            // Calcuna a nova remperatura
            antoine.setConstantes(composicao[0].antoine.A, composicao[0].antoine.B, composicao[0].antoine.C);
            let novobolt = antoine.calcularTemperatura(pjsat) + 273.15;
            e = this.erroRelativo(bolt, novobolt);
            bolt = novobolt;
        }

        // Organiza a resposta
        let sistema: { substancia: Substancia, fracaoLiq: number, fracaoGas: number }[] = [];
        composicao.forEach((comp, i) => {
            sistema.push({
                substancia: comp.substancia,
                fracaoLiq: comp.fracao,
                fracaoGas: fracaoGas[i]
            });
        });

        return {
            temperatura: bolt, pressao, sistema
        };
    }

    orvp(temperatura: number, composicao: ComposicaoUA[]) {
        // ATENÇÃO: AS FRAÇÕES PASSADAS SÃO CORRESPONDENTES A FRASE GÁS
        // Definição de temperatura e classes de cálculo
        const unifac = new UNIFAC();
        const antoine = new Antoine("Genérico", "CH4");
        unifac.setTemperatura(temperatura);

        // Calcula a primeira estimativa da pressão
        let soma = 0;
        let psat: number[] = [];
        let gama: number[] = [];
        for(let i = 0; i < composicao.length; i++) {
            antoine.setConstantes(composicao[i].antoine.A, composicao[i].antoine.B, composicao[i].antoine.C);
            gama.push(1);
            psat.push(antoine.calcularPressao(temperatura - 273.15));
            soma += composicao[i].fracao / (gama[i] * psat[i]);
        }
        let orvp = 1 / soma;

        // Estimativa inicial das frações na fase líquida
        let fracaoLiq: number[] = [];
        for(let i = 0; i < composicao.length; i++) {
            fracaoLiq.push(composicao[i].fracao * orvp / (gama[i] * psat[i]));
        }

        // Define o sistema para o unifac
        composicao.forEach((comp, i) => {
            unifac.add({
                substancia: comp.substancia, subgrupos: comp.subgrupos, fracao: fracaoLiq[i]
            });
        });
        gama = unifac.calcular();

        // Inicializa o processo iterativo
        let e = 1;
        while(e > this.erro) {
            // Calcula uma nova estimativa pra pressão
            soma = 0;
            for(let i = 0; i < composicao.length; i++) {
                soma += composicao[i].fracao / (gama[i] * psat[i]);
            }
            orvp = 1 / soma;

            // Calcula novas frações na fase líquida
            for(let i = 0; i < composicao.length; i++) {
                fracaoLiq[i] = composicao[i].fracao * orvp / (gama[i] * psat[i]);
            }

            // Recalcula os coeficientes de atividade
            unifac.reset();
            composicao.forEach((comp, i) => {
                unifac.add({
                    substancia: comp.substancia, subgrupos: comp.subgrupos, fracao: fracaoLiq[i]
                });
            });
            gama = unifac.calcular();

            // Calcula a pressão de iteração
            soma = 0;
            for(let i = 0; i < composicao.length; i++) {
                soma += composicao[i].fracao / (gama[i] * psat[i]);
            }
            let novoorvp = 1 / soma;
            e = this.erroRelativo(orvp, novoorvp);
            orvp = novoorvp;
        }

        // Organiza a resposta
        let sistema: { substancia: Substancia, fracaoLiq: number, fracaoGas: number }[] = [];
        composicao.forEach((comp, i) => {
            sistema.push({
                substancia: comp.substancia,
                fracaoLiq: fracaoLiq[i],
                fracaoGas: comp.fracao
            });
        });

        return {
            temperatura, pressao: orvp, sistema
        };
    }

    orvt(pressao: number, composicao: ComposicaoUA[]) {
        // ATENÇÃO: AS FRAÇÕES PASSADAS SÃO CORRESPONDENTES A FRASE GÁS
        // Instancia as classes de controle
        const antoine = new Antoine("Genérico", "CH4");
        const unifac = new UNIFAC();

        // Encontra os primeiros estimadores para temperatura
        let soma = 0;
        let tsat: number[] = [];
        let gama: number[] = [];
        for(let i = 0; i < composicao.length; i++) {
            antoine.setConstantes(composicao[i].antoine.A, composicao[i].antoine.B, composicao[i].antoine.C);
            gama.push(1);
            tsat.push(antoine.calcularTemperatura(pressao) + 273.15);
            soma += composicao[i].fracao * tsat[i];
        }
        let orvt = soma;

        // Encontra as pressões de saturação
        let psat: number[] = [];
        composicao.forEach(comp => {
            antoine.setConstantes(comp.antoine.A, comp.antoine.B, comp.antoine.C);
            psat.push(antoine.calcularPressao(orvt - 273.15));
        });

        // Definição da variável de iteração muda
        let pjsat = psat[0];
        soma = 0;
        for(let i = 0; i < composicao.length; i++) {
            soma += composicao[i].fracao * pjsat / (gama[i] * psat[i]);
        }
        pjsat = pressao * soma;

        // Calcula uma nova temperatura
        antoine.setConstantes(composicao[0].antoine.A, composicao[0].antoine.B, composicao[0].antoine.C);
        orvt = antoine.calcularTemperatura(pjsat) + 273.15;

        // Calcula novas pressões de saturação e frações no líquido
        let fracaoLiq: number[] = [];
        for(let i = 0; i < composicao.length; i++) {
            antoine.setConstantes(composicao[i].antoine.A, composicao[i].antoine.B, composicao[i].antoine.C);
            psat[i] = antoine.calcularPressao(orvt - 273.15);
            fracaoLiq.push(composicao[i].fracao * pressao / (gama[i] * psat[i]));
        }

        // Define o sistema para cálculo do coeficiente de atividade
        unifac.setTemperatura(orvt);
        composicao.forEach((comp, i) => {
            unifac.add({
                substancia: comp.substancia, subgrupos: comp.subgrupos, fracao: fracaoLiq[i]
            });
        });
        gama = unifac.calcular();

        // Calcula novo valor para variável de iteração muda
        soma = 0;
        pjsat = psat[0];
        for(let i = 0; i < composicao.length; i++) {
            soma += composicao[i].fracao * pjsat / (gama[i] * psat[i]);
        }
        pjsat = pressao * soma;

        // Calcula nova temperatura
        antoine.setConstantes(composicao[0].antoine.A, composicao[0].antoine.B, composicao[0].antoine.C);
        orvt = antoine.calcularTemperatura(pjsat) + 273.15;

        // Inica o processo iterativo
        let e = 1;
        while(e > this.erro) {
            // Calcula novas pressões de saturação
            for(let i = 0; i < composicao.length; i++) {
                antoine.setConstantes(composicao[i].antoine.A, composicao[i].antoine.B, composicao[i].antoine.C);
                psat[i] = antoine.calcularPressao(orvt - 273.15);
                fracaoLiq[i] = composicao[i].fracao * pressao / (gama[i] * psat[i]);
            }

            // Calcula novos coeficientes de atividade
            unifac.reset();
            unifac.setTemperatura(orvt);
            composicao.forEach((comp, i) => {
                unifac.add({
                    substancia: comp.substancia, subgrupos: comp.subgrupos, fracao: fracaoLiq[i]
                });
            });
            gama = unifac.calcular();

            // Calcula nova variável de iteração muda
            pjsat = psat[0];
            soma = 0;
            for(let i = 0; i < composicao.length; i++) {
                soma += composicao[i].fracao * pjsat / (gama[i] * psat[i]);
            }
            pjsat = pressao * soma;

            // Calcula nova temperatura
            antoine.setConstantes(composicao[0].antoine.A, composicao[0].antoine.B, composicao[0].antoine.C);
            let novoorvt = antoine.calcularTemperatura(pjsat) + 273.15;
            e = this.erroRelativo(orvt, novoorvt);
            orvt = novoorvt;
        }

        // Organiza a resposta
        let sistema: { substancia: Substancia, fracaoLiq: number, fracaoGas: number }[] = [];
        composicao.forEach((comp, i) => {
            sistema.push({
                substancia: comp.substancia,
                fracaoLiq: fracaoLiq[i],
                fracaoGas: comp.fracao
            });
        });

        return {
            temperatura: orvt, pressao, sistema
        };
    }

}